// FILE: ThreadDirectoryRow.tsx
// Purpose: One thread in the directory — who owns it, where it lives, when it last moved.
// Layer: UI component
// Exports: ThreadDirectoryRow, ThreadDirectoryRowSkeleton

import type {
  ProviderKind,
  ThreadDirectoryEntry,
  UserDirectoryEntry,
  UserId,
} from "@taskin-code/contracts";

import { ArchiveIcon, BotIcon, GitPullRequestIcon } from "~/lib/icons";
import { formatRelativeTime } from "~/lib/relativeTime";
import { cn } from "~/lib/utils";
import { useVisibleManagedAgentsById } from "~/agents/managedAgentsReactQuery";
import { AgentAvatar } from "~/components/AgentAvatar";
import { UserAvatar } from "~/components/UserAvatar";

/**
 * The model slug as somebody says it out loud.
 *
 * Same treatment as the provider/model picker's `formatModelSlug`, kept local rather than shared
 * with it: that one formats options a user is about to choose from a live catalogue, this one
 * formats whatever string a thread was actually persisted with, including slugs the catalogue no
 * longer offers. Neither should start deciding the other's rules.
 */
const formatModelSlug = (slug: string): string =>
  slug
    .replace(/^gpt-/i, "GPT-")
    .replace(/^claude-/i, "Claude ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const PROVIDER_LABELS: Record<ProviderKind, string> = {
  claudeAgent: "Claude",
  codex: "Codex",
};

/**
 * What runs the thread, in one phrase.
 *
 * The agent's name wins the label when there is one: "Revisor de PRs" says more about what the
 * thread is than "Claude Opus 4.6" does, and the model rides along after it. With no agent it is
 * the model alone, and with neither — an unreadable selection — nothing renders at all rather than
 * an empty pill.
 */
const formatRuntimeLabel = (entry: ThreadDirectoryEntry): string | null => {
  // Nullish and not just null: a client that outlives a server deploy — or outruns one, in dev —
  // is handed rows without these fields at all, and a row that renders without its runtime line is
  // a far better answer than a directory that will not draw.
  const model = entry.model == null ? null : formatModelSlug(entry.model);
  const providerLabel = entry.provider == null ? null : (PROVIDER_LABELS[entry.provider] ?? null);
  const modelLabel = model ?? providerLabel;
  const agentName = entry.agent?.name ?? null;
  if (agentName !== null) {
    return modelLabel === null ? agentName : `${agentName} · ${modelLabel}`;
  }
  return modelLabel;
};

/** A calendar date, for the "created" column — relative time is already carried by the other one. */
const formatDay = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export function ThreadDirectoryRow(props: {
  readonly entry: ThreadDirectoryEntry;
  readonly projectName: string | null;
  readonly usersById: ReadonlyMap<UserId, UserDirectoryEntry>;
  readonly onOpen: (entry: ThreadDirectoryEntry) => void;
}) {
  const { entry, projectName, usersById, onOpen } = props;
  const author = entry.createdBy === null ? null : (usersById.get(entry.createdBy) ?? null);
  // A thread whose author predates authorship being recorded, or whose account is gone. Named
  // rather than left blank: an empty cell reads as a rendering bug.
  const authorLabel =
    author?.displayName ?? (entry.createdBy === null ? "Sin autor" : "Desconocido");
  const runtimeLabel = formatRuntimeLabel(entry);
  const visibleAgentsById = useVisibleManagedAgentsById();
  const managedAgent = entry.agent ? visibleAgentsById.get(entry.agent.agentId) : undefined;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(entry)}
        className="group flex w-full items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
      >
        <UserAvatar
          userId={entry.createdBy ?? "unknown"}
          displayName={authorLabel}
          avatarFileName={author?.avatarFileName ?? null}
          size="sm"
          className="shrink-0"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            {/* The running dot goes before the title so a scan down the column answers "what is
                working right now" without reading a word. */}
            {entry.isRunning ? (
              <span
                className="size-1.5 shrink-0 rounded-full bg-emerald-400"
                aria-label="En ejecución"
              />
            ) : null}
            <span className="truncate text-sm text-foreground">{entry.title}</span>
            {entry.linkedPullRequest ? (
              <GitPullRequestIcon
                className="size-3.5 shrink-0 text-muted-foreground/70"
                aria-label={`PR #${entry.linkedPullRequest.number}`}
              />
            ) : null}
            {entry.archivedAt ? (
              <ArchiveIcon
                className="size-3.5 shrink-0 text-muted-foreground/60"
                aria-label="Archivado"
              />
            ) : null}
          </span>

          <span className="flex w-full items-center gap-1.5 truncate text-xs text-muted-foreground/72">
            <span className="truncate">{authorLabel}</span>
            {projectName ? <span aria-hidden>·</span> : null}
            {projectName ? <span className="truncate">{projectName}</span> : null}
            {entry.assignedUserIds.length > 0 ? <span aria-hidden>·</span> : null}
            {entry.assignedUserIds.length > 0 ? (
              <span className="shrink-0">
                compartido con {entry.assignedUserIds.length}{" "}
                {entry.assignedUserIds.length === 1 ? "persona" : "personas"}
              </span>
            ) : null}
          </span>

          {/* What runs it. Its own line rather than another `·` on the one above: that line answers
              "whose work is this", and a model slug crowded into it pushes the project name out of
              a narrow window before either is read. */}
          {runtimeLabel ? (
            <span className="flex items-center gap-1 truncate text-xs text-muted-foreground/60">
              {entry.agent ? (
                <AgentAvatar
                  agentId={entry.agent.agentId}
                  displayName={entry.agent.name}
                  avatarFileName={managedAgent?.avatarFileName ?? null}
                  size="xs"
                  className="size-3 text-[7px]"
                />
              ) : (
                <BotIcon className="size-3 shrink-0" aria-hidden />
              )}
              <span className="truncate">{runtimeLabel}</span>
            </span>
          ) : null}

          {/* The matched fragment, only when the read carried a query. Marked by `«…»` on the
              server rather than by HTML, so it is drawn as the text it is. */}
          {entry.snippet ? (
            <span className="line-clamp-2 text-xs text-muted-foreground/60">{entry.snippet}</span>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs text-muted-foreground/72">
          <span className={cn(entry.isRunning ? "text-foreground/80" : undefined)}>
            {formatRelativeTime(entry.updatedAt)}
          </span>
          <span className="text-muted-foreground/50">creado {formatDay(entry.createdAt)}</span>
        </div>
      </button>
    </li>
  );
}

export function ThreadDirectoryRowSkeleton() {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-border/50 px-3 py-2.5">
      <div className="size-6 shrink-0 animate-pulse rounded-full bg-muted/50" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted/50" />
        <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted/40" />
      </div>
      <div className="h-2.5 w-16 shrink-0 animate-pulse rounded bg-muted/40" />
    </li>
  );
}
